import type { CSSProperties } from 'react';

export interface TemplateOption {
  id: string;
  name: string;
  description: string;
  columnCount: number;
  columns: Array<{
    name: string;
    color: string;
    order: number;
  }>;
  isPredefined: boolean;
}

interface TemplateSelectorProps {
  selectedTemplateId: string | null;
  onTemplateSelect: (templateId: string | null) => void;
  className?: string;
}

const templates: TemplateOption[] = [
  {
    id: 'custom',
    name: 'Custom',
    description: 'Start with an empty board and create your own columns',
    columnCount: 0,
    columns: [],
    isPredefined: false,
  },
  {
    id: 'start-stop-continue',
    name: 'Start/Stop/Continue',
    description: 'Traditional retrospective format focusing on what to start, stop, and continue doing',
    columnCount: 3,
    columns: [
      { name: 'Start', color: '#10B981', order: 1 },
      { name: 'Stop', color: '#EF4444', order: 2 },
      { name: 'Continue', color: '#3B82F6', order: 3 },
    ],
    isPredefined: true,
  },
  {
    id: '4ls',
    name: '4Ls',
    description: 'Liked, Learned, Lacked, Longed for - comprehensive retrospective format',
    columnCount: 4,
    columns: [
      { name: 'Liked', color: '#10B981', order: 1 },
      { name: 'Learned', color: '#3B82F6', order: 2 },
      { name: 'Lacked', color: '#F59E0B', order: 3 },
      { name: 'Longed for', color: '#8B5CF6', order: 4 },
    ],
    isPredefined: true,
  },
  {
    id: 'kalm',
    name: 'KALM',
    description: 'Keep, Add, Less, More - simple and effective retrospective format',
    columnCount: 4,
    columns: [
      { name: 'Keep', color: '#10B981', order: 1 },
      { name: 'Add', color: '#3B82F6', order: 2 },
      { name: 'Less', color: '#F59E0B', order: 3 },
      { name: 'More', color: '#8B5CF6', order: 4 },
    ],
    isPredefined: true,
  },
  {
    id: 'sailboat',
    name: 'Sailboat',
    description: 'Wind, Anchor, Island, Rocks - visual retrospective format',
    columnCount: 4,
    columns: [
      { name: 'Wind', color: '#06B6D4', order: 1 },
      { name: 'Anchor', color: '#F59E0B', order: 2 },
      { name: 'Island', color: '#10B981', order: 3 },
      { name: 'Rocks', color: '#EF4444', order: 4 },
    ],
    isPredefined: true,
  },
];

function getColumnColorStyle(color: string): CSSProperties {
  return {
    backgroundColor: color,
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    display: 'inline-block',
  };
}

export function TemplateSelector({
  selectedTemplateId,
  onTemplateSelect,
  className = '',
}: TemplateSelectorProps): JSX.Element {
  return (
    <div className={`space-y-4 ${className}`}>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Choose a Template (Optional)
        </label>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Select a template to pre-populate your board with columns, or choose "Custom" to start from scratch.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.map((template) => (
          <div
            key={template.id}
            data-testid={`template-card-${template.id}`}
            className={`
              relative border-2 rounded-xl p-5 cursor-pointer transition-all duration-200 min-h-[140px]
              shadow-sm
              ${selectedTemplateId === template.id
                ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/20 ring-2 ring-blue-500 ring-opacity-50 shadow-md'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md hover:bg-gradient-to-br hover:from-gray-50 hover:to-blue-50/30 dark:hover:from-gray-800 dark:hover:to-blue-900/10'
              }
            `}
            onClick={() => onTemplateSelect(template.id)}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-2 mb-2">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    {template.name}
                  </h3>
                  {template.isPredefined && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 whitespace-nowrap">
                      Template
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
                  {template.description}
                </p>
                {template.columnCount > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                      {template.columnCount} column{template.columnCount !== 1 ? 's' : ''}:
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      {template.columns.map((column) => (
                        <div key={`${template.id}-${column.order}`} className="flex items-center gap-1.5">
                          <div style={getColumnColorStyle(column.color)} className="flex-shrink-0" />
                          <span className="text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            {column.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-shrink-0 pt-0.5">
                <div
                  className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200
                    ${selectedTemplateId === template.id
                      ? 'border-blue-500 bg-blue-500 shadow-sm'
                      : 'border-gray-300 dark:border-gray-600'
                    }
                  `}
                >
                  {selectedTemplateId === template.id && (
                    <div className="w-2.5 h-2.5 bg-white rounded-full shadow-sm" />
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedTemplateId && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-blue-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm text-blue-700 dark:text-blue-300">
              {selectedTemplateId === 'custom'
                ? "You'll start with an empty board and can add columns as needed."
                : 'This template will be applied to your board, creating the predefined columns.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
